// RemixManagePhotos.js
// @author: anghello

import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Row, Col, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useAuth } from '../../context/auth/AuthContext';
import { toast } from 'react-toastify';
import PSO from '../../services/PSO';
import Strings from '../../translations/Translations';
import MediaStatuses from '../../enums/MediaStatuses';
import AppleStoreImage from '../../assets/img/apple_pp_store-267x89.png';
import PlayStoreImage from '../../assets/img/google-play-badge-300x89.png';
import MediaThumb1 from '../media/MediaThumb1';
import ModalPhoto1 from '../modals/ModalPhoto1';
import Button1 from '../buttons/Button1';

const REMIX_APP_IOS_LINK = process.env.REACT_APP_REMIX_APP_IOS_LINK;
const REMIX_APP_ANDROID_LINK = process.env.REACT_APP_REMIX_APP_ANDROID_LINK;
const DEFAULT_ENMAX = 64;
const DEFAULT_ESORT = 2;

export default function RemixManagePhotos({ remixPars }) {
  const { authenticated, toggleLoading } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [currentPhoto, setCurrentPhoto] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFilter, setCurrentFilter] = useState(MediaStatuses[0]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [lastMedia, setLastMedia] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const [photoModal, setPhotoModal] = useState(false);
  const togglePhotoModal = useCallback(() => {
    setPhotoModal(!photoModal);
  }, [setPhotoModal, photoModal]);

  const getPhotos = useCallback(async (key = 0) => {
    if (remixPars && remixPars.rid) {
      setIsLoading(true);
      try {
        let response;
  
        if (key === 0) {
          response = await PSO.getRemixApprovedElements({
            un: authenticated.un,
            al: authenticated.al,
            rid: remixPars.rid,
            enmax: DEFAULT_ENMAX,
            esort: DEFAULT_ESORT
          });
        } else if (key === 1) {
          response = await PSO.getRemixNonApprovedElements({
            un: authenticated.un,
            al: authenticated.al,
            rid: remixPars.rid,
            enmax: DEFAULT_ENMAX,
            estatus: 6
          });
        } else if (key === 2) {
          response = await PSO.getRemixNonApprovedElements({
            un: authenticated.un,
            al: authenticated.al,
            rid: remixPars.rid,
            enmax: DEFAULT_ENMAX,
            estatus: 5
          });
        } else if (key === 3) {
          response = await PSO.getStickyElementIDListInRemix({
            un: authenticated.un,
            al: authenticated.al,
            rid: remixPars.rid,
            enmax: DEFAULT_ENMAX,
            etype: 1
          });
        }
  
        if (response.result === 'success') {
          if (response.its_media) {
            const media = response.its_media.map((value) => {
              if (key === 0) {
                value.to_authorize = 0;
              } else {
                value.to_authorize = 1;
              }
              return value;
            });
            setPhotos(media);
            setShowLoadMore(media && media.length === DEFAULT_ENMAX);
          } else if (response.its) {
            setPhotos(response.its);
          }

          setLastMedia({
            eidul: response.lastmediaeidul,
            esticknul: response.lastmediaesticknul,
            esvul: response.lastmediaesvul
          });
        } else {
          const message = (response && response.error) ? response.error : Strings('error_on_request');
          toast.error(message);
        }
  
        setIsLoading(false);
      } catch (err) {
        toast.error(Strings('general_error'));
        setIsLoading(false);
      }
    }
  }, [remixPars, authenticated, setIsLoading]);

  const getMorePhotos = useCallback(async (key = 0) => {
    if (remixPars && remixPars.rid) {
      toggleLoading(true);
      try {
        let response;

        const pars = {
          un: authenticated.un,
          al: authenticated.al,
          rid: remixPars.rid,
          enmax: DEFAULT_ENMAX,
          esort: DEFAULT_ESORT
        };

        if (lastMedia) {
          pars.eidul = lastMedia.eidul;
          pars.esticknul = lastMedia.esticknul;
          pars.esvul = lastMedia.esvul;
        }

        if (key === 0) {
          response = await PSO.getRemixApprovedElements(pars);
        } else if (key === 1) {
          pars.estatus = 6;
          response = await PSO.getRemixNonApprovedElements(pars);
        } else if (key === 2) {
          pars.estatus = 5;
          response = await PSO.getRemixNonApprovedElements(pars);
        } else if (key === 3) {
          pars.etype = 1;
          response = await PSO.getStickyElementIDListInRemix(pars);
        }

        if (response.result === 'success') {
          if (response.its_media) {
            const media = response.its_media.map((value) => {
              if (key === 0) {
                value.to_authorize = 0;
              } else {
                value.to_authorize = 1;
              }
              return value;
            });
            setPhotos([...photos, ...media]);

            const isToShowLoadMore = (response.its_media.length === DEFAULT_ENMAX);
            setShowLoadMore(isToShowLoadMore);
          } else if (response.its) {
            setPhotos([...photos, ...response.its]);
          }

          setLastMedia({
            eidul: response.lastmediaeidul,
            esticknul: response.lastmediaesticknul,
            esvul: response.lastmediaesvul
          });
        } else {
          const message = (response && response.error) ? response.error : Strings('error_on_request');
          toast.error(message);
        }
      } catch (err) {
        toast.error(Strings('general_error'));
      }

      toggleLoading(false);
    }
  }, [remixPars, authenticated, photos, setPhotos, setShowLoadMore, toggleLoading, lastMedia]);

  const applyFilter = useCallback(async (value) => {
    if (value.key !== currentFilter.key) {
      setPhotos([]);
      setCurrentFilter(value);
      getPhotos(value.key);
    }
  }, [setCurrentFilter, currentFilter, getPhotos, setPhotos]);

  useEffect(() => { getPhotos(); }, [getPhotos]);

  return (
    <Fragment>
      <Row className='mb-3'>
        <Col>
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color="light">
              {currentFilter.label}
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem header>{Strings('filter_photos')}</DropdownItem>
              {MediaStatuses.map((value) => (
                <DropdownItem
                  key={value.key}
                  onClick={() => applyFilter(value)}
                >
                  {value.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </Col>
      </Row>
      <Row>
        {photos && photos.length === 0 && currentFilter.key !== 3 &&  (
          <Col>
            <div className="mt-2 alert alert-secondary fade show" role="alert">
              {Strings('no_photos')}
            </div>
          </Col>
        )}

        {currentFilter.key === 3 && (
          <Col>
            <div className="mt-2 alert alert-info fade show" role="alert">
              <p>
                {Strings('manage_sticky_photos')}
              </p>
              <p>
                <a href={REMIX_APP_IOS_LINK} target="_blank" rel="noopener noreferrer">
                  <img src={AppleStoreImage} className="mr-2 get_app_image_button" alt={Strings('download_remix_app_for_ios')} />
                </a>

                <a href={REMIX_APP_ANDROID_LINK} target="_blank" rel="noopener noreferrer">
                  <img src={PlayStoreImage} className="get_app_image_button" alt={Strings('download_remix_app_for_android')} />
                </a>
              </p>
            </div>
          </Col>
        )}

      <Col>
        {photos && (
          <Row>
            {photos && photos.map((value, index) => (
              <Col sm={12} md={2} lg={2} key={value.eid}>
                <MediaThumb1
                  photo={value}
                  callback={() => {
                    setCurrentPhoto(value);
                    setCurrentIndex(index);
                    togglePhotoModal();
                  }}
                />
              </Col>
            ))}
          </Row>
        )}
      </Col>

      {isLoading && !showLoadMore && (
        <Col sm={12} className='d-flex justify-content-center mt-3'>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </Col>
      )}

      {showLoadMore && photos && (
        <Col sm={12} className='d-flex justify-content-center mt-3'>
          <Button1
            className='pb-btn-1'
            size='sm'
            onClick={() => getMorePhotos(currentFilter.key)}
            loading={isLoading}
            disabled={isLoading}
          >
            {Strings('load_more')}
          </Button1>
        </Col>
      )}

      </Row>

      <ModalPhoto1
        open={photoModal}
        setOpen={setPhotoModal}
        photo={currentPhoto}
        remixPars={remixPars}
        refresh={() => { getPhotos(currentFilter.key); }}
        showPrevious={currentIndex > 0}
        showNext={currentIndex < photos.length - 1}
        previous={() => {
          const newIndex = currentIndex - 1;
          setCurrentPhoto(photos[newIndex]);
          setCurrentIndex(newIndex);
        }}
        next={() => {
          const newIndex = currentIndex + 1;
          setCurrentPhoto(photos[newIndex]);
          setCurrentIndex(newIndex);
        }}
      />

    </Fragment>
  );
}
